import React, { useCallback, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { Button, Stack } from "@mui/material"

import {
  PSE_STATUS,
  showProductionStepsSupervision,
  loadProductionStepExecution,
  PSEGoOneStepBack,
  updateProductionStepExecutionStatusToDone,
  // updateProductionStepExecutionLots,
  saveProductionStepsCoolingInformations,
  saveProductionStepExecutionEndCoolingInformations,
  saveProductionStepExecutionEndCookingInformations,
  updateProductionStepExecutionSubStatus,
  saveProductionStepExecutionWeightValidationInformation,
  regeneratePSEReproduction, saveMergedPSEWeightValidationInformation
} from "../../actions/ProductionSteps/ProductionStepExecutions"
import ProductionStepExecutionFooter from "./ProductionStepExecutionFooter"
import PSEProgressBar from "./preview/PSEProgressBar"
import { calculateNextSubStatus, getInsufficientWeight, getPriorStepData, PSE_SUBSTATUSES } from "../../utils/productionStepExecution"
// import ProductionStepExecutionLotsSelection, { productionStepExecutionLotsSelectionFormId } from "./productionStepExecutionInProgressCases/ProductionStepExecutionLotsSelection"
import ProductionStepExecutionStepCompletion from "./productionStepExecutionInProgressCases/ProductionStepExecutionStepCompletion"
import ProductionStepExecutionTemperatureValidation from "./productionStepExecutionInProgressCases/ProductionStepExecutionTemperatureValidation"
import ProductionStepExecutionWeightValidation, { productionStepExecutionWeightValidationFormId } from "./productionStepExecutionInProgressCases/ProductionStepExecutionWeightValidation"
import { StyledMainContainer } from "./styledComponents"
import ProductionStepsExecutionFixedHeader from "./ProductionSteoExecutionsFixedHeader"
import ProductionStepExecutionInsufficientWeight from "./productionStepExecutionInProgressCases/ProductionStepExecutionInsufficientWeight"
import EditInsufficientWeightDialogForm from "./productionStepExecutionInProgressCases/EditInsufficientWeightDialogForm"
import StepCompletionCookingsFooter from "./StepCompletionCookingsFooter"
import { productionStepExecutionCoolingEndFormId, productionStepExecutionCookingEndFormId } from "./productionStepExecutionInProgressCases/ProductionStepExecutionMachinesForm"
import { BUTTON_STATUS } from "../../utils/constant"
import { confirmAction } from "./confirmAction"

const hasCookingPSEFamilyIngredientFish = (productionStepExecution, transformationType) => {
  return ["COOKING", "RACK_LOADING"].includes(transformationType) && productionStepExecution.hasFamilyIngredientFish
}

const ProductionStepExecutionInProgress = ({
  productionStepExecution
}) => {
  const [openInsufficientWeightDialog, setOpenInsufficientWeightDialog] = useState(false)
  const [hasWeightMergedFormErrors, setHasWeightMergedFormErrors] = useState(false)
  const [disabledButton, setDisabledButton] = useState(false)
  const transformationType = useMemo(() => productionStepExecution.productionStepSnapshot.transformation, [productionStepExecution])
  
  // For transformation types that don't require machine forms (like DRAINING, UNPACKING, etc.),
  // enable the button by default. For COOKING/COOLING, it will be enabled by the machine form.
  const initialButtonStatus = useMemo(() => {
    if (productionStepExecution.subStatus === PSE_SUBSTATUSES.stepCompletion && 
        !["COOKING", "COOLING"].includes(transformationType)) {
      return BUTTON_STATUS.ENABLED
    }
    return BUTTON_STATUS.DISABLED
  }, [productionStepExecution.subStatus, transformationType])
  
  // 0 : disabled, 1 : enabled, -1 : saveOnly
  const [nextButtonStatus, setNextButtonStatusState] = useState(initialButtonStatus)
  const statusHasChanged = useRef(false)

  const setNextButtonStatus = (status) => {
    setNextButtonStatusState(status)
    statusHasChanged.current = true
  }


  const dispatch = useDispatch()

  const handleBack = () => {
    if (nextButtonStatus !== BUTTON_STATUS.DISABLED
      && (productionStepExecution.subStatus == PSE_SUBSTATUSES.temperatureValidation
        || (productionStepExecution.subStatus == PSE_SUBSTATUSES.stepCompletion
          && (transformationType === "COOLING" || transformationType === "COOKING")
        )
      )
      && statusHasChanged.current) {
      return confirmAction(
        "Vous êtes sur le point de quitter cette étape.\rCertaines machines ne sont pas encore complétées.\rSouhaitez-vous enregistrer votre saisie ?",
        undefined
        , () => {
          dispatch(showProductionStepsSupervision())
        }
      )
    }
    dispatch(showProductionStepsSupervision())
  }
  const handleStepBack = async () => {
    await PSEGoOneStepBack(productionStepExecution.objectId || productionStepExecution.id)
    dispatch(loadProductionStepExecution({ id: productionStepExecution.objectId || productionStepExecution.id }))
  }

  const handleConfirmPSEToDone = useCallback(() => {
    const ulteriorStep = productionStepExecution.ulteriorStep
    const isLastBeforeTechnicalSectionLastStep = (
      ulteriorStep?.isSectionLastStep
      && ulteriorStep?.productionStepSnapshot?.transformation === "TECHNICAL"
    )
    const formattedValues = {
      status: (productionStepExecution.isSectionLastStep || isLastBeforeTechnicalSectionLastStep)
        ? PSE_STATUS.toTest
        : PSE_STATUS.done
    }
    dispatch(updateProductionStepExecutionStatusToDone(productionStepExecution.objectId, formattedValues))
  }, [productionStepExecution])

  const handleSubStatusChange = useCallback(() => {
    const newSubStatus = calculateNextSubStatus(productionStepExecution)
    if (newSubStatus) {
      dispatch(updateProductionStepExecutionSubStatus({ id: productionStepExecution.objectId, newSubStatus }))
    }
    if (!newSubStatus) {
      handleConfirmPSEToDone()
    }
  }, [productionStepExecution])

  /** KFC-2268 hide lotsSelection case temporarily **/
  // const handleConfirmLotsForm = useCallback((values) => {
  //   const nextSubStatus = calculateNextSubStatus(productionStepExecution)
  //   const { secondaryDLCs, lots } = values
  //   dispatch(updateProductionStepExecutionLots({
  //     id: productionStepExecution.objectId,
  //     secondaryDLCsIds: secondaryDLCs || [],
  //     lotsIds: lots || [],
  //     newSubStatus: nextSubStatus
  //   }))
  // }, [productionStepExecution])


  const handleConfirmCoolingForm = useCallback((values) => {
    const newSubStatus = nextButtonStatus === BUTTON_STATUS.SAVE_ONLY ? null : calculateNextSubStatus(productionStepExecution)
    dispatch(saveProductionStepsCoolingInformations({
      productionStepExecutionId: productionStepExecution.objectId,
      machinesBatch: values.machinesBatch,
      newSubStatus
    }))
    if (!newSubStatus && nextButtonStatus !== BUTTON_STATUS.SAVE_ONLY) {
      handleConfirmPSEToDone()
    }
  }, [productionStepExecution, nextButtonStatus])

  const handleConfirmEndCoolingForm = useCallback((values) => {
    const newSubStatus = nextButtonStatus === BUTTON_STATUS.SAVE_ONLY ? null : calculateNextSubStatus(productionStepExecution)
    dispatch(saveProductionStepExecutionEndCoolingInformations({
      productionStepExecutionId: productionStepExecution.objectId,
      machinesBatch: values.machinesBatch,
      newSubStatus
    }))
    if (!newSubStatus && nextButtonStatus !== BUTTON_STATUS.SAVE_ONLY) {
      handleConfirmPSEToDone()
    }
  }, [productionStepExecution, nextButtonStatus])


  const handleConfirmEndCookingForm = useCallback((values) => {
    const newSubStatus = nextButtonStatus === BUTTON_STATUS.SAVE_ONLY ? null : calculateNextSubStatus(productionStepExecution)
    dispatch(saveProductionStepExecutionEndCookingInformations({
      productionStepExecutionId: productionStepExecution.objectId,
      machinesBatch: values.machinesBatch,
      newSubStatus
    }))
    if (!newSubStatus && nextButtonStatus !== BUTTON_STATUS.SAVE_ONLY) {
      handleConfirmPSEToDone()
    }
  }, [productionStepExecution, nextButtonStatus])

  const handleConfirmWeightValidation = useCallback(async (values) => {
    if (values.netWeightMerged === null) {
      setHasWeightMergedFormErrors(true)
    }

    let hasError = false
    if (productionStepExecution.isStepMerged && !productionStepExecution.ulteriorStep) {
      const individualPSENetweightTotal = (values.netWeightMerged || []).reduce((sum, obj) => sum + (obj.value || 0), 0)

      const diff = Math.abs((individualPSENetweightTotal || 0) - (values.netWeight || 0))
      // use a tolerance of 0.01 kg to account for rounding errors instead of exact equality
      hasError = diff < 0 || diff > 0.01
    } else {
      hasError = values.hasError
    }

    if (!(productionStepExecution.isStepMerged && !productionStepExecution.ulteriorStep) || !hasError) {
      /** THIS "IF ELSE" in order TO NOT BREAK PRODUCTION **/
      if ((productionStepExecution.isStepMerged && !productionStepExecution.ulteriorStep)) {
        let newSubStatus = calculateNextSubStatus(productionStepExecution)

        await dispatch(saveMergedPSEWeightValidationInformation({
          productionStepExecutionId: productionStepExecution.objectId,
          netWeightValues: values,
          sectionOutputWeightsByPSE: (values.sectionOutputWeightsByPSE && Object.keys(values.sectionOutputWeightsByPSE).length > 0) ? values.sectionOutputWeightsByPSE : undefined,
          newSubStatus
        }))
      } else {
        const isInsufficientWeight = getInsufficientWeight(values.netWeight, productionStepExecution.theoreticalNetWeight)

        let newSubStatus = calculateNextSubStatus(productionStepExecution)

        if (isInsufficientWeight) {
          newSubStatus = PSE_SUBSTATUSES.insufficientWeight
        }
        const dataToSave = {
          productionStepExecutionId: productionStepExecution.objectId,
          netWeight: values.netWeight,
          newSubStatus
        }
        if (
          values.sectionOutputWeights &&
          typeof values.sectionOutputWeights === "object" &&
          Object.keys(values.sectionOutputWeights).length > 0
        ) {
          dataToSave.sectionOutputWeights = values.sectionOutputWeights
        }
        if (
          values.packagingDateWeights &&
          typeof values.packagingDateWeights === "object" &&
          Object.values(values.packagingDateWeights).some(v => v != null)
        ) {
          dataToSave.packagingDateWeights = Object.fromEntries(
            Object.entries(values.packagingDateWeights).filter(([, v]) => v != null)
          )
        }
        await dispatch(saveProductionStepExecutionWeightValidationInformation(dataToSave))

        if (!newSubStatus) {
          handleConfirmPSEToDone()
        }
      }
    }
  }, [productionStepExecution])


  const handleCloseInsufficientWeightDialog = () => setOpenInsufficientWeightDialog(false)

  const handleConfirmInsufficientWeight = async (values) => {
    dispatch(regeneratePSEReproduction(productionStepExecution.objectId, null, values))
  }

  const renderRightActions = useCallback(() => {
    if (!productionStepExecution) return
    const buttonText = nextButtonStatus === BUTTON_STATUS.SAVE_ONLY ? "Enregistrer et continuer plus tard" : calculateNextSubStatus(productionStepExecution) ? "Suivant" : "Terminer"


    switch (productionStepExecution.subStatus) {
      /** KFC-2268 hide lotsSelection case temporarily **/
      // case PSE_SUBSTATUSES.lotsSelection:
      //   return (
      //     <Button variant="contained" form={productionStepExecutionLotsSelectionFormId} type="submit">
      //       {buttonText}
      //     </Button>
      //   )
      case PSE_SUBSTATUSES.stepCompletion:
        if (transformationType === "COOLING") {
          return (
            <Button
              variant="contained"
              form={productionStepExecutionCoolingEndFormId}
              type="submit"
              disabled={nextButtonStatus === 0}
            >
              {buttonText}
            </Button>
          )
        }
        else if (transformationType === "COOKING") {
          return (
            <StepCompletionCookingsFooter
              hasCookingPSEFamilyIngredientFish={hasCookingPSEFamilyIngredientFish(productionStepExecution, transformationType)}
              buttonText={buttonText}
              pseId={productionStepExecution.objectId}
              onSubStatusChange={handleSubStatusChange}
            />
          )
        }
        else {
          // For other transformation types (DRAINING, UNPACKING, etc.)
          // that don't require special handling, just show a simple button
          return (
            <Button
              variant="contained"
              onClick={handleSubStatusChange}
            >
              {buttonText}
            </Button>
          )
        }
      case PSE_SUBSTATUSES.temperatureValidation:
        if (transformationType === "COOKING") {
          return (
            <Button
              variant="contained"
              type="submit"
              form={productionStepExecutionCookingEndFormId}
              disabled={nextButtonStatus === BUTTON_STATUS.DISABLED}
            >
              {buttonText}
            </Button>
          )
        }
        if (transformationType === "COOLING") {
          return (
            <Button
              variant="contained"
              form={productionStepExecutionCoolingEndFormId}
              type="submit"
              disabled={nextButtonStatus === BUTTON_STATUS.DISABLED}
            >
              {buttonText}
            </Button>
          )
        }
        else return null
      case PSE_SUBSTATUSES.weightValidation:
        return (
          <Button
            variant="contained"
            form={productionStepExecutionWeightValidationFormId}
            type="submit"
            disabled={disabledButton}
          >
            {buttonText}
          </Button>
        )
      default:
        return null
    }

  }, [productionStepExecution.subStatus, transformationType, nextButtonStatus, disabledButton])

  const _getMainContent = useCallback(() => {
    const transformationType = productionStepExecution.productionStepSnapshot.transformation
    switch (productionStepExecution.subStatus) {
      /** KFC-2268 hide lotsSelection case temporarily **/
      // case PSE_SUBSTATUSES.lotsSelection:
      //   return (
      //     <ProductionStepExecutionLotsSelection
      //       productionStepExecution={productionStepExecution}
      //       handleSubmit={handleConfirmLotsForm}
      //       priorStepData={getPriorStepData(productionStepExecution)}
      //     />
      //   )
      case PSE_SUBSTATUSES.stepCompletion:
        return (
          <ProductionStepExecutionStepCompletion
            productionStepExecution={productionStepExecution}
            handleSubmit={handleConfirmCoolingForm}
            priorStepData={getPriorStepData(productionStepExecution)}
            setNextButtonStatus={setNextButtonStatus}
          />
        )
      case PSE_SUBSTATUSES.temperatureValidation:
        return (
          <ProductionStepExecutionTemperatureValidation
            productionStepExecution={productionStepExecution}
            handleSubmit={transformationType === "COOKING" ? handleConfirmEndCookingForm : handleConfirmEndCoolingForm}
            priorStepData={getPriorStepData(productionStepExecution)}
            setNextButtonStatus={setNextButtonStatus}
          />
        )
      case PSE_SUBSTATUSES.weightValidation:
        return (
          <>
            <ProductionStepExecutionWeightValidation
              productionStepExecution={productionStepExecution}
              handleSubmit={handleConfirmWeightValidation}
              hasWeightMergedFormErrors={hasWeightMergedFormErrors}
              onDisableButton={setDisabledButton}
            />
          </>
        )
      case PSE_SUBSTATUSES.insufficientWeight:
        return (
          <ProductionStepExecutionInsufficientWeight
            productionStepExecution={productionStepExecution}
            handleSubmit={handleConfirmCoolingForm}
            priorStepData={getPriorStepData(productionStepExecution)}
          />
        )
      default:
        return null
    }
  }, [productionStepExecution.subStatus, nextButtonStatus])

  return (
    <>
      {/* header */}
      <ProductionStepsExecutionFixedHeader onBack={handleBack} />

      {/* body */}
      <StyledMainContainer sx={{ paddingTop: 0 }}>
        <Stack spacing={3} sx={{ p: 3, borderBottom: "1px solid #E0E0E0" }}>
          <PSEProgressBar
            currentSubStatus={productionStepExecution.subStatus}
            productionStepExecution={productionStepExecution}
            visible={[PSE_STATUS.inProgress, PSE_STATUS.todo].includes(productionStepExecution.status)}
            times={{
              startTime: productionStepExecution.startTime,
              endTime: productionStepExecution.endTime,
              theoreticalStartTime: productionStepExecution.theoreticalStartTime,
              theoreticalEndTime: productionStepExecution.theoreticalEndTime
            }}
          />
        </Stack>
        <Stack
          spacing={3}
          sx={{
            p: 3,
            pb: hasCookingPSEFamilyIngredientFish(productionStepExecution, transformationType) ? 12 : 3,
          }}
        >
          {_getMainContent()}
        </Stack>
      </StyledMainContainer>

      <EditInsufficientWeightDialogForm
        onClose={handleCloseInsufficientWeightDialog}
        open={openInsufficientWeightDialog}
        productionStepExecution={productionStepExecution}
        onConfirm={handleConfirmInsufficientWeight}
      />

      {/* footer */}
      <ProductionStepExecutionFooter
        rightAction={renderRightActions()}
        leftAction={
          <Button variant="outlined" onClick={handleStepBack}>
            Retour
          </Button>
        }
        justifyBetween={true}
      />
    </>
  )
}

export default ProductionStepExecutionInProgress